<template>
    <subscription-dropdown
        ref="subscriptionDropdown"
        aria-haspopup
        :is-impersonating="isImpersonating"
        @open="handleSubscriptionOpen"
        @close="handleSubscriptionClose" />
    <div class="right-options">
        <pendo-tools-dropdown
            ref="pendoTools"
            :aria-current="navItemActive(TOP_LEVEL_NAV_OPTIONS.PENDO_TOOLS) && 'page'" />
        <pendo-products-dropdown
            ref="pendoProducts"
            :aria-current="navItemActive(TOP_LEVEL_NAV_OPTIONS.PENDO_PRODUCTS) && 'page'" />
        <settings-dropdown
            ref="settings"
            :aria-current="navItemActive(TOP_LEVEL_NAV_OPTIONS.SETTINGS) && 'page'" />
        <notifications-dropdown
            v-if="hasNotificationsAccess"
            ref="notifications"
            :aria-current="navItemActive(TOP_LEVEL_NAV_OPTIONS.NOTIFICATIONS) && 'page'" />
        <user-dropdown
            ref="user"
            :aria-current="navItemActive(TOP_LEVEL_NAV_OPTIONS.USER) && 'page'" />
    </div>
    <div class="right-options__popover">
        <pendo-popover-v2
            ref="moreOptions"
            :title="$t('topNav.More options')"
            placement="bottom-end"
            :hover="false"
            :popper-class="['right-options__popover-wrapper']"
            keyboard-navigation
            @show="handleMoreOptionsShow"
            @hide="closeMoreMenu">
            <template #default="{ shown, ...triggerAttrs }">
                <pendo-button
                    v-pendo-tooltip="{
                        content: $t('topNav.More options'),
                        arrow: true,
                    }"
                    v-bind="triggerAttrs"
                    class="top-nav__trigger top-nav__button-trigger"
                    :class="{ 'is-open': shown }"
                    type="tertiary"
                    size="small"
                    pendo-analytics="more-options"
                    :aria-label="$t('topNav.More options')">
                    <pendo-icon
                        type="more-horizontal"
                        size="20" />
                </pendo-button>
            </template>
            <template #popover>
                <div
                    ref="moreOptionsContent"
                    class="right-options__popover-content">
                    <pendo-tools-dropdown
                        ref="pendoTools"
                        class="more-options__popover"
                        :aria-current="navItemActive(TOP_LEVEL_NAV_OPTIONS.PENDO_TOOLS) && 'page'"
                        prevent-tab-key-navigation
                        @select="closeMoreMenu" />
                    <pendo-products-dropdown
                        ref="pendoProducts"
                        class="more-options__popover"
                        :aria-current="navItemActive(TOP_LEVEL_NAV_OPTIONS.PENDO_PRODUCTS) && 'page'"
                        prevent-tab-key-navigation
                        @select="closeMoreMenu" />
                    <settings-dropdown
                        ref="settings"
                        class="more-options__popover"
                        :aria-current="navItemActive(TOP_LEVEL_NAV_OPTIONS.SETTINGS) && 'page'"
                        prevent-tab-key-navigation
                        @select="closeMoreMenu" />
                    <notifications-dropdown
                        v-if="hasNotificationsAccess"
                        ref="notifications"
                        class="more-options__popover"
                        :aria-current="navItemActive(TOP_LEVEL_NAV_OPTIONS.NOTIFICATIONS) && 'page'"
                        prevent-tab-key-navigation
                        @select="closeMoreMenu" />
                    <user-dropdown
                        ref="user"
                        class="more-options__popover"
                        :aria-current="navItemActive(TOP_LEVEL_NAV_OPTIONS.USER) && 'page'"
                        prevent-tab-key-navigation
                        @select="closeMoreMenu" />
                </div>
            </template>
        </pendo-popover-v2>
    </div>
</template>

<script>
import SubscriptionDropdown from '@/top-nav/components/subscriptions/subscription-dropdown.vue';
import UserDropdown from '@/top-nav/components/user-dropdown/user-dropdown.vue';
import PendoToolsDropdown from '@/top-nav/components/pendo-tools/pendo-tools-dropdown.vue';
import PendoProductsDropdown from '@/top-nav/components/pendo-products/pendo-products-dropdown.vue';
import NotificationsDropdown from '@/top-nav/components/notifications/notifications-dropdown.vue';
import SettingsDropdown from '@/top-nav/components/settings/settings-dropdown.vue';
import { PendoPopoverV2, PendoButton, PendoIcon, PendoTooltip } from '@pendo/components';
import { BREAKPOINTS } from '@pendo/less-variables/lib/utils/breakpoints';

import { TOP_LEVEL_NAV_OPTIONS } from '@/constants/nav';
import { isOptionActive } from '@/side-nav/helpers/side-nav.helper';

export default {
    name: 'TopNavRightOptionsWrapper',
    components: {
        SubscriptionDropdown,
        UserDropdown,
        PendoToolsDropdown,
        PendoProductsDropdown,
        NotificationsDropdown,
        SettingsDropdown,
        PendoPopoverV2,
        PendoButton,
        PendoIcon,
    },
    directives: {
        PendoTooltip,
    },
    props: {
        isImpersonating: {
            type: Boolean,
            default: false,
        },
    },
    data () {
        return {
            TOP_LEVEL_NAV_OPTIONS,
            largeScreenDropdownStates: {
                subscriptions: false,
            },
            smallScreenDropdownStates: {
                subscriptions: false,
            },
        };
    },
    computed: {
        hasNotificationsAccess () {
            return this.$subFlag('veltIntegration') && this.$segmentFlag('veltMigration');
        },
        activeNavOption () {
            return this.$route?.meta?.activeNavOption || {};
        },
    },
    created () {
        window.addEventListener('resize', this.closeAllDropdowns);
    },
    beforeUnmount () {
        window.removeEventListener('resize', this.closeAllDropdowns);
    },
    methods: {
        closeAllDropdowns () {
            this.$refs.pendoTools?.hide?.();
            this.$refs.pendoProducts?.hide?.();
            this.$refs.settings?.hide?.();
            this.$refs.notifications?.hide?.();
            this.$refs.user?.hide?.();
            if (window.innerWidth > BREAKPOINTS.X_LARGE) {
                if(this.largeScreenDropdownStates.subscriptions) {
                    this.$refs.subscriptionDropdown.toggleDropdown();
                    this.largeScreenDropdownStates.subscriptions = false;
                }
            } else {
                if(this.smallScreenDropdownStates.subscriptions) {
                    this.$refs.subscriptionDropdown.toggleDropdown();
                    this.smallScreenDropdownStates.subscriptions = false;

                }
                this.closeMoreMenu();
            }
        },
        closeMoreMenu () {
            this.$refs.moreOptions?.hide?.();
            this.$refs.moreOptions?.getTriggerElement()?.focus();
        },
        navItemActive (navItemName) {
            return isOptionActive(navItemName, this.activeNavOption.topOptionName);
        },
        handleMoreOptionsShow () {
            this.smallScreenDropdownStates.subscriptions = false;
            setTimeout(() => {
                const firstOption = this.$refs.moreOptionsContent?.querySelector('.pendo-popover-v2__reference');
                if (firstOption) {
                    firstOption.querySelector('.top-nav__trigger').focus();
                }
            }, 50);
        },
        handleSubscriptionOpen () {
            if (window.innerWidth <= BREAKPOINTS.X_LARGE) {
                this.closeMoreMenu();
                this.smallScreenDropdownStates.subscriptions = true;
            } else {
                this.largeScreenDropdownStates.subscriptions = true;
            }
        },
        handleSubscriptionClose () {
            if (window.innerWidth <= BREAKPOINTS.X_LARGE) {
                this.smallScreenDropdownStates.subscriptions = false;
            } else {
                this.largeScreenDropdownStates.subscriptions = false;
            }
        },
    },
};
</script>

<style lang="less" scoped>
@import '@/side-nav/nav.style.less';

.right-options {
    display: flex;
    gap: 6px;
    align-items: center;

    &__popover {
        display: none;

        .screen-size(@zero, @x-large, {
            display: block;
        });
    }

    &__popover-content {
        display: flex;
        gap: 6px;
    }

    .screen-size(@zero, @x-large, {
        display: none;
    });
}
</style>

<style lang="less">
.right-options__popover-wrapper {
    .pendo-popover-v2__panel {
        background-color: var(--palette-gray-5);
    }
}
</style>